import { useState } from "react";
import { NpdCreateModal } from "./NpdCreateModal";

const RESULT_STYLE = {
  PASS:        { background: "#dcfce7", color: "#15803d" },
  FAIL:        { background: "#fee2e2", color: "#dc2626" },
  IN_PROGRESS: { background: "#fef9c3", color: "#92400e" },
};

function ResultBadge({ value }) {
  const s = RESULT_STYLE[value] ?? { background: "#f1f5f9", color: "#475569" };
  const label = value === "IN_PROGRESS" ? "In Progress" : value;
  return (
    <span style={{ ...s, borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>
      {label}
    </span>
  );
}

export function NpdDetailModal({ trial, canEdit, canDelete, onUpdate, onDelete, onClose, rms = [], sfgs = [] }) {
  const [t,       setT]       = useState(trial);
  const [editing, setEditing] = useState(false);
  const [deleting,setDeleting]= useState(false);
  const [error,   setError]   = useState(null);

  async function handleSave(body) {
    const updated = await onUpdate(t.id, body);
    if (updated) setT(updated);
    setEditing(false);
  }

  async function handleDelete() {
    if (!window.confirm(`Remove trial ${t.trialNo}? This action cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(t.id);
      onClose();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  }

  if (editing) {
    return (
      <div className="overlay">
        <NpdCreateModal trial={t} onSave={handleSave} onCancel={() => setEditing(false)} rms={rms} sfgs={sfgs} />
      </div>
    );
  }

  const info = (label, value) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14 }}>{value || <span style={{ color: "#cbd5e1" }}>—</span>}</div>
    </div>
  );

  const sectionStyle = {
    fontWeight: 700, fontSize: 12, color: "#1a3a6b",
    borderBottom: "2px solid #1a3a6b", marginBottom: 10, paddingBottom: 4, marginTop: 18,
  };

  return (
    <div className="modal" style={{ width: 860 }}>
      <div className="modal-header">
        <div>
          <div className="modal-title">{t.trialNo}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{t.prodCode}</div>
        </div>
        <button className="modal-close" onClick={onClose}>✕</button>
      </div>

      <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {error && <div className="alert-warn" style={{ marginBottom: 12 }}>{error}</div>}

        {/* Header row */}
        <div className="form-row" style={{ flexWrap: "wrap", gap: 0 }}>
          <div style={{ flex: 1, minWidth: 160 }}>{info("Product Code", t.prodCode)}</div>
          <div style={{ flex: 1, minWidth: 130 }}>{info("Date", t.date)}</div>
          <div style={{ flex: 1, minWidth: 130 }}>{info("Sample Batch Qty", t.sampleBatchQty)}</div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
              Result
            </div>
            <ResultBadge value={t.result} />
          </div>
        </div>

        {/* Ingredients */}
        {t.ingredients?.length > 0 && (
          <>
            <div style={sectionStyle}>Ingredients</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={th}>#</th>
                  <th style={th}>Ingredient Name</th>
                  <th style={th}>Qty / %</th>
                </tr>
              </thead>
              <tbody>
                {t.ingredients.map(i => (
                  <tr key={i.seqNo}>
                    <td style={td}>{i.seqNo}</td>
                    <td style={td}>{i.name}</td>
                    <td style={td}>{i.qtyPct ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Viscometer & Other Params */}
        {(t.viscometerParams || t.otherParams) && (
          <>
            <div style={sectionStyle}>Parameters</div>
            <div className="form-row" style={{ gap: 24, alignItems: "flex-start" }}>
              {t.viscometerParams && (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
                    Viscometer Parameters
                  </div>
                  <pre style={{ fontFamily: "inherit", whiteSpace: "pre-wrap", fontSize: 13, margin: 0 }}>
                    {t.viscometerParams}
                  </pre>
                </div>
              )}
              {t.otherParams && (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
                    Other Parameters
                  </div>
                  <pre style={{ fontFamily: "inherit", whiteSpace: "pre-wrap", fontSize: 13, margin: 0 }}>
                    {t.otherParams}
                  </pre>
                </div>
              )}
            </div>
          </>
        )}

        {/* Observations */}
        {t.observations?.length > 0 && (
          <>
            <div style={sectionStyle}>Observations</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={th}>#</th>
                  <th style={th}>Parameter</th>
                  <th style={th}>Standard Value</th>
                  <th style={th}>Observed Value</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {t.observations.map(o => (
                  <tr key={o.seqNo}>
                    <td style={td}>{o.seqNo}</td>
                    <td style={{ ...td, fontWeight: 500 }}>{o.parameter}</td>
                    <td style={td}>{o.standardValue ?? "—"}</td>
                    <td style={td}>{o.observedValue ?? "—"}</td>
                    <td style={td}>
                      {o.okStatus ? (
                        <span style={{
                          borderRadius: 4, padding: "1px 6px", fontSize: 11, fontWeight: 600,
                          background: o.okStatus === "OK" ? "#dcfce7" : o.okStatus === "NOT OK" ? "#fee2e2" : "#f1f5f9",
                          color:      o.okStatus === "OK" ? "#15803d" : o.okStatus === "NOT OK" ? "#dc2626" : "#64748b",
                        }}>
                          {o.okStatus}
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Remarks */}
        {t.remarks && (
          <>
            <div style={sectionStyle}>Remarks</div>
            <p style={{ fontSize: 14, margin: 0 }}>{t.remarks}</p>
          </>
        )}

        {/* Meta */}
        <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid #f1f5f9",
          fontSize: 11, color: "#94a3b8", display: "flex", gap: 24 }}>
          <span>Created by <strong>{t.createdBy}</strong></span>
          <span>at {t.createdAt ? new Date(t.createdAt).toLocaleString("en-AE", { timeZone: "Asia/Dubai" }) : "—"}</span>
        </div>
      </div>

      <div className="modal-footer">
        {canDelete && (
          <button className="btn btn-danger" disabled={deleting} onClick={handleDelete}>
            {deleting ? "Removing…" : "Remove Trial"}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            Edit Trial
          </button>
        )}
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

const th = {
  textAlign: "left", padding: "6px 8px", fontWeight: 600,
  borderBottom: "1px solid #e2e8f0", fontSize: 12,
};
const td = { padding: "5px 8px", borderBottom: "1px solid #f1f5f9" };
